/**
 * The last few API calls, held for the report form — issue bata-platform#686.
 *
 * A person reporting a problem should not have to know what a correlation id is. The transport
 * sends one with every call (`X-Correlation-Id`, `./http.ts`) and writes it here with the
 * operation and the status it got back; the report form reads the list and sends it with the
 * report, so the team can go from a report straight to the platform's logs and the error tracker.
 *
 * **Held in the page, nowhere else.** Nothing here touches `localStorage`, a cookie or the
 * network: a reload empties it, which is right — the ids of last week's calls explain nothing
 * about today's problem, and a list that survived a sign-out would carry one person's activity
 * into the next person's session. It holds what the person did, not what they saw: no URL, no
 * parameter, no body.
 *
 * **Why a status of 0.** A call that never reached the service (a network failure) has no status;
 * it is recorded as `0`, which no HTTP response can be, so the report says "unreachable" without
 * a second field. A call the caller aborted is not recorded: it is a decision of the screen, not
 * an outcome, and a report listing it would point the team at a request nobody was waiting on.
 */

/** How many calls are kept. The issue asks for the last five — enough to reach the failing one. */
export const CALL_LOG_SIZE = 5;

export interface RecordedCall {
  /** The `X-Correlation-Id` the call was sent with. */
  readonly correlationId: string;
  /** The generated operation's id, e.g. `list_changes_api_v1_registry_changes_get`. */
  readonly operationId: string;
  /** The HTTP status, or `0` when the service was never reached. */
  readonly status: number;
  /** When the outcome was known, as an ISO-8601 instant. */
  readonly at: string;
}

let calls: readonly RecordedCall[] = [];

/** Appends one call, dropping the oldest beyond {@link CALL_LOG_SIZE}. */
export function recordCall(call: RecordedCall): void {
  calls = [...calls, call].slice(-CALL_LOG_SIZE);
}

/** The recorded calls, oldest first. A copy: the caller cannot change the log by holding it. */
export function recentCalls(): readonly RecordedCall[] {
  return [...calls];
}

/** Empties the log. For tests: the app itself never clears it. */
export function clearCallLog(): void {
  calls = [];
}
